import { ICON_BASE_PATH } from "@/app/utils/config";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface OtploginProps {
  onEditEmail: () => void;
}

const Otp: React.FC<OtploginProps> = ({ onEditEmail }) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(180);
  const [isError, setIsError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };
  const navigate = useNavigate();

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;

    setOtp(newOtp);
    setIsError(false);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setIsError(true);
      return;
    }

    setIsLoading(true);

    try {
      const storedOtp = sessionStorage.getItem("email-otp");

      if (enteredOtp !== storedOtp) {
        setIsError(true);
        setSuccessMessage("");
        return;
      }

      sessionStorage.setItem("otpVerified", "true");

      setIsError(false);
      setSuccessMessage("OTP verified successfully!");

      setTimeout(() => {
        navigate("/onboarding/setPassword");
      }, 1000);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = () => {
    setSecondsLeft(180);

    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();

    sessionStorage.setItem("email-otp", newOtp);

    alert("OTP has been resent!");
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");

    const s = (sec % 60).toString().padStart(2, "0");

    return `${m}:${s}`;
  };

  const handlediteamil = () => {
    onEditEmail();
  };

  useEffect(() => {
    const storedEmail = sessionStorage.getItem("email") || "";

    setEmail(storedEmail);

    if (secondsLeft <= 0) return;

    const timerId = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [secondsLeft]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <div className="col-span-12">
      <div className="gx-login-verify">
        <h1 className="gx-h2-l">
          Verify your <span className="gx-h2-b">Email</span>
          <p className="gx-p14-md">
            Enter the 6-digit code sent to
            <span
              className="underline gx-p14-b cursor-pointer"
              title="Edit Email"
              onClick={handlediteamil}
            >
              {email}

              <img src={`${ICON_BASE_PATH}editicon.svg`} alt="Edit Email" />
            </span>
          </p>
        </h1>
      </div>

      <div className="flex gap-3 gx-otp-box">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={setInputRef(index)}
            type="text"
            maxLength={1}
            value={digit}
            placeholder="0"
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
              }

              if (e.key === "Enter" && otp.every((d) => d !== "")) {
                handleVerify();
              }
            }}
            className={`w-12 h-12 text-center text-lg font-bold rounded-lg outline-none ${
              isError
                ? "border-2 border-red-600 bg-red-100"
                : "border-2 border-gray-400 focus:border-black focus:ring-1 focus:ring-black"
            }`}
          />
        ))}
      </div>

      {isError && <p className="validation-error">Enter a valid OTP</p>}

      {successMessage && (
        <p className="validation-error-green">{successMessage}</p>
      )}

      {!successMessage && (
        <div className="gx-resend-otp">
          <a onClick={handleResendOtp}>
            I didn’t receive a code ({formatTime(secondsLeft)})
          </a>
        </div>
      )}

      <div className="login-continue-btn">
        {isLoading ? (
          <div className="gx-disabled-bnt">
            <button disabled>Loading...</button>
          </div>
        ) : (
          <button
            onClick={handleVerify}
            disabled={otp.some((digit) => digit === "")}
            className={`${
              otp.some((digit) => digit === "") ? "gx-btn-disabled" : ""
            } transition-all`}
          >
            Verify OTP
          </button>
        )}
      </div>

      <div className="login-back-btn">
        <button onClick={handlediteamil}>
          <img src={`${ICON_BASE_PATH}backicon.svg`} alt="Back" />
          BACK
        </button>
      </div>
    </div>
  );
};

export default Otp;
